import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const CalendarEvents = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    title: '', description: '', event_type: 'other',
    start_date: '', end_date: '', is_all_day: true,
  });

  useEffect(() => { fetchEvents(); }, [currentDate, user?.school_id]);

  const getSelectedAcademicYearId = () => {
    const schoolId = user?.school_id;
    if (!schoolId) return null;
    return localStorage.getItem(`selected_academic_year_id_${schoolId}`);
  };

  useEffect(() => {
    const handler = () => fetchEvents();
    window.addEventListener('academicYearChanged', handler);
    return () => window.removeEventListener('academicYearChanged', handler);
  }, [currentDate, user?.school_id]);

  const fetchEvents = async () => {
    try {
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      const academicYearId = getSelectedAcademicYearId();
      const schoolId = user?.school_id;
      
      const response = await api.get('/events', {
        params: {
          start_date: startOfMonth.toISOString().split('T')[0],
          end_date: endOfMonth.toISOString().split('T')[0],
          ...(schoolId ? { school_id: schoolId } : {}),
          ...(academicYearId ? { academic_year_id: academicYearId } : {}),
        }
      });
      setEvents(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch events');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const academicYearId = getSelectedAcademicYearId();
      const schoolId = user?.school_id;
      await api.post('/events', {
        ...formData,
        start_date: formData.start_date + 'T00:00:00',
        end_date: (formData.end_date || formData.start_date) + 'T23:59:59',
        ...(schoolId ? { school_id: schoolId } : {}),
        ...(academicYearId ? { academic_year_id: academicYearId } : {}),
      });
      toast({ title: 'Success', description: 'Event created successfully' });
      setIsDialogOpen(false);
      resetForm();
      fetchEvents();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.message || 'Failed', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '', description: '', event_type: 'other',
      start_date: '', end_date: '', is_all_day: true,
    });
    setSelectedDate(null);
  };

  const openNewEvent = (date = null) => {
    if (date) {
      setFormData(prev => ({
        ...prev,
        start_date: date.toISOString().split('T')[0],
        end_date: date.toISOString().split('T')[0],
      }));
    }
    setIsDialogOpen(true);
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDay = firstDay.getDay();
    const totalDays = lastDay.getDate();
    
    const days = [];
    
    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({ day: prevMonthLastDay - i, currentMonth: false });
    }
    
    // Current month days
    for (let i = 1; i <= totalDays; i++) {
      days.push({ day: i, currentMonth: true, date: new Date(year, month, i) });
    }
    
    // Next month days
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ day: i, currentMonth: false });
    }
    
    return days;
  };

  const toDateOnly = (value) => {
    if (!value) return null;
    const s = String(value);
    return s.split('T')[0].split(' ')[0];
  };

  const getEventsForDay = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(event => {
      const eventStart = toDateOnly(event.start || event.start_date);
      const eventEnd = toDateOnly(event.end || event.end_date) || eventStart;
      if (!eventStart) return false;
      return dateStr >= eventStart && dateStr <= eventEnd;
    });
  };

  const days = generateCalendarDays();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const eventTypes = [
    { value: 'academic', label: 'Academic', color: '#3b82f6' },
    { value: 'cultural', label: 'Cultural', color: '#8b5cf6' },
    { value: 'sports', label: 'Sports', color: '#22c55e' },
    { value: 'meeting', label: 'Meeting', color: '#f59e0b' },
    { value: 'holiday', label: 'Holiday', color: '#ef4444' },
    { value: 'other', label: 'Other', color: '#6b7280' },
  ];

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Calendar</h1>
          <p className="text-slate-400">Manage events and schedules</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-event-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Event
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-800 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle>Create New Event</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="bg-slate-700 border-slate-600" required />
              </div>
              <div>
                <Label>Description</Label>
                <Input value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="bg-slate-700 border-slate-600" />
              </div>
              <div>
                <Label>Event Type</Label>
                <Select value={formData.event_type} onValueChange={(v) => setFormData({...formData, event_type: v})}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    {eventTypes.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date *</Label>
                  <Input type="date" value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    className="bg-slate-700 border-slate-600" required />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input type="date" value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    className="bg-slate-700 border-slate-600" />
                </div>
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">Create Event</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Calendar Header */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigateMonth(-1)} className="text-slate-400 hover:text-white">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <h2 className="text-xl font-semibold text-white">
              {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h2>
            <Button variant="ghost" onClick={() => navigateMonth(1)} className="text-slate-400 hover:text-white">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Calendar Grid */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          {/* Week days header */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map(day => (
              <div key={day} className="text-center text-sm font-medium text-slate-400 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((day, index) => {
              const dayEvents = day.date ? getEventsForDay(day.date) : [];
              const isToday = day.date && day.date.toDateString() === new Date().toDateString();
              
              return (
                <div
                  key={index}
                  onClick={() => day.currentMonth && openNewEvent(day.date)}
                  className={`min-h-24 p-2 rounded-lg cursor-pointer transition-colors ${
                    day.currentMonth 
                      ? isToday 
                        ? 'bg-blue-600/20 border border-blue-500' 
                        : 'bg-slate-700/50 hover:bg-slate-700'
                      : 'bg-slate-900/50 text-slate-600'
                  }`}
                >
                  <span className={`text-sm font-medium ${
                    day.currentMonth ? isToday ? 'text-blue-400' : 'text-white' : 'text-slate-600'
                  }`}>
                    {day.day}
                  </span>
                  <div className="mt-1 space-y-1">
                    {dayEvents.slice(0, 2).map((event, i) => (
                      <div
                        key={i}
                        className="text-xs px-1 py-0.5 rounded truncate"
                        style={{ backgroundColor: event.color + '30', color: event.color }}
                      >
                        {event.title}
                      </div>
                    ))}
                    {dayEvents.length > 2 && (
                      <div className="text-xs text-slate-400">+{dayEvents.length - 2} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Event Type Legend */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            {eventTypes.map(type => (
              <div key={type.value} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: type.color }}></div>
                <span className="text-sm text-slate-400">{type.label}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CalendarEvents;
